/**
 * KB Manager Modal
 *
 * Provides a modal interface for browsing and managing knowledge bases.
 * Features: list KBs, create/delete KBs, edit descriptions, browse/edit chunks.
 */

import { getHeaders } from './api.js';
import { showToast } from './toast.js';
import { escapeHtml } from './html-escape.js';
import { confirm } from './confirm-dialog.js';

/**
 * Show a toast notification
 */

// State
let kbs = [];
let selectedKB = null;
let currentView = 'list';  // 'list' | 'chunks' | 'editor'
let currentTab = 'chunks'; // 'chunks' | 'imports'
let chunksData = { chunks: [], total: 0, page: 1, pages: 1 };
let editingChunk = null;
let isCreatingChunk = false;  // true when creating new chunk, false when editing
let savedScrollTop = 0;       // Scroll position in chunk list before entering editor
let modalOpen = false;

// Docling import state
let doclingFile = null;
let doclingImportActive = false;
let doclingAbortController = null;
let doclingUploadMeta = null;
let doclingTimerInterval = null;
let doclingTimerStart = null;
const DOCLING_ALLOWED_EXT = new Set(['.pdf','.docx','.pptx','.xlsx','.html','.htm','.md']);

// DOM elements cache
const elements = {};

/**
 * Initialize the KB Manager
 */
export function initKBManager() {
    createModalHTML();
    cacheElements();
    setupEventListeners();
}

/**
 * Get KB count for badge
 */
export async function getKBCount() {
    try {
        const response = await fetch('/kb', { headers: getHeaders(false) });
        if (!response.ok) return 0;
        const data = await response.json();
        return data.count || 0;
    } catch {
        return 0;
    }
}

/**
 * Create the modal HTML structure
 */
function createModalHTML() {
    const modalHTML = `
        <div id="kb-manager-overlay" class="modal-overlay"></div>
        <div id="kb-manager-panel" class="kb-manager-panel">
            <div class="kb-manager-header">
                <div class="kb-manager-nav">
                    <button id="kb-back-btn" class="kb-back-btn hidden">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                        Back
                    </button>
                    <h2 id="kb-manager-title">Knowledge Bases</h2>
                </div>
                <button id="kb-manager-close" class="modal-close">&times;</button>
            </div>

            <!-- List View -->
            <div id="kb-list-view" class="kb-view">
                <div class="kb-manager-toolbar">
                    <input type="text" id="kb-search" class="kb-search" placeholder="Search knowledge bases...">
                    <button id="kb-create-btn" class="kb-create-btn" title="New KB">+</button>
                </div>
                <div id="kb-list" class="kb-list">
                    <div class="kb-loading">Loading...</div>
                </div>
            </div>

            <!-- KB Detail View (tabbed) -->
            <div id="kb-chunks-view" class="kb-view hidden">
                <div class="kb-detail-tabs">
                    <button class="kb-tab active" data-tab="chunks">Chunks</button>
                    <button class="kb-tab" data-tab="imports">Imports</button>
                </div>
                <div id="kb-tab-chunks" class="kb-tab-content">
                    <div class="chunks-toolbar">
                        <input type="text" id="chunk-search" class="kb-search" placeholder="Search chunks...">
                        <span id="chunk-count" class="chunk-count"></span>
                        <button id="batch-delete-btn" class="kb-batch-delete-btn hidden">Delete Selected</button>
                        <button id="add-chunk-btn" class="kb-create-btn">+ Add Chunk</button>
                    </div>
                    <div id="chunk-list" class="chunk-list"></div>
                    <div class="chunks-pagination">
                        <button id="chunks-first" class="pagination-btn" title="First page">«</button>
                        <button id="chunks-prev" class="pagination-btn">← Prev</button>
                        <span id="chunks-page-info"></span>
                        <button id="chunks-next" class="pagination-btn">Next →</button>
                        <button id="chunks-last" class="pagination-btn" title="Last page">»</button>
                    </div>
                </div>
                <!-- Batch delete confirmation -->
                <div id="batch-delete-confirm" class="kb-confirm-overlay hidden">
                    <div class="kb-confirm-dialog">
                        <p id="batch-delete-msg">Delete selected chunks?</p>
                        <div class="kb-confirm-actions">
                            <button id="batch-delete-cancel" class="btn-secondary">Cancel</button>
                            <button id="batch-delete-go" class="btn-danger">Delete</button>
                        </div>
                    </div>
                </div>
                <div id="kb-tab-imports" class="kb-tab-content hidden">
                    <div class="imports-toolbar">
                        <span id="imports-count" class="chunk-count"></span>
                        <button id="imports-import-btn" class="kb-create-btn" title="Import document with Docling">
                            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                            Import
                        </button>
                    </div>
                    <div id="imports-list" class="imports-list"></div>
                </div>
            </div>

            <!-- Chunk Editor View -->
            <div id="kb-editor-view" class="kb-view hidden">
                <div class="chunk-editor">
                    <div class="editor-field">
                        <label>Content</label>
                        <textarea id="chunk-content" rows="10"></textarea>
                    </div>
                    <div class="editor-field">
                        <label class="editor-toggle-label">
                            <input type="checkbox" id="chunk-summary-toggle">
                            Summary
                        </label>
                        <textarea id="chunk-summary" rows="3" disabled></textarea>
                    </div>
                    <div class="editor-field">
                        <label class="editor-toggle-label">
                            <input type="checkbox" id="chunk-questions-toggle">
                            Questions (one per line)
                        </label>
                        <textarea id="chunk-questions" rows="4" disabled></textarea>
                    </div>
                    <div class="editor-footer">
                        <button id="chunk-cancel" class="btn-secondary">Cancel</button>
                        <button id="chunk-delete" class="btn-danger">Delete Chunk</button>
                        <button id="chunk-save" class="btn-primary">Save Changes</button>
                    </div>
                </div>
            </div>

            <!-- Create KB Dialog (overlay within modal) -->
            <div id="kb-create-dialog" class="kb-dialog hidden">
                <div class="kb-dialog-content">
                    <h3>Create Knowledge Base</h3>
                    <div class="dialog-field">
                        <label>Name</label>
                        <input type="text" id="new-kb-name" placeholder="My Knowledge Base">
                        <span class="field-hint">Will be converted to a URL-friendly slug</span>
                    </div>
                    <div class="dialog-field">
                        <label>Description (optional)</label>
                        <input type="text" id="new-kb-description" placeholder="What this KB contains...">
                    </div>
                    <div class="dialog-buttons">
                        <button id="create-cancel" class="btn-secondary">Cancel</button>
                        <button id="create-confirm" class="btn-primary">Create</button>
                    </div>
                </div>
            </div>

            <!-- Delete Confirmation Dialog -->
            <div id="kb-delete-dialog" class="kb-dialog hidden">
                <div class="kb-dialog-content">
                    <h3>Delete Knowledge Base</h3>
                    <p id="delete-kb-message">Are you sure you want to delete this KB?</p>
                    <p class="warning-text">This action cannot be undone.</p>
                    <div class="dialog-buttons">
                        <button id="delete-cancel" class="btn-secondary">Cancel</button>
                        <button id="delete-confirm" class="btn-danger">Delete</button>
                    </div>
                </div>
            </div>

            <!-- Edit Description Dialog -->
            <div id="kb-desc-dialog" class="kb-dialog hidden">
                <div class="kb-dialog-content">
                    <h3>Edit Description</h3>
                    <div class="dialog-field">
                        <label>Description</label>
                        <textarea id="edit-kb-description" rows="3"></textarea>
                    </div>
                    <div class="dialog-buttons">
                        <button id="desc-cancel" class="btn-secondary">Cancel</button>
                        <button id="desc-confirm" class="btn-primary">Save</button>
                    </div>
                </div>
            </div>

            <!-- Docling Import Dialog -->
            <div id="docling-import-dialog" class="kb-dialog hidden">
                <div class="kb-dialog-content field-editor docling-import-editor">
                    <div class="field-editor-header">
                        <h3>Import Document</h3>
                        <button class="field-editor-close" id="docling-import-close">&times;</button>
                    </div>
                    <div class="docling-import-body">
                        <!-- Step 1: Upload -->
                        <div id="docling-step-upload" class="docling-step">
                            <div class="db-import-dropzone" id="docling-dropzone">
                                <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                <p>Drop document here</p>
                                <span>PDF, DOCX, PPTX, XLSX, HTML, Markdown, LaTeX</span>
                                <input type="file" id="docling-file-input" accept=".pdf,.docx,.pptx,.xlsx,.html,.htm,.md" hidden>
                            </div>
                            <div id="docling-file-info" class="db-import-file-info hidden">
                                <span id="docling-file-name"></span>
                                <button class="field-action-btn" id="docling-file-clear" title="Remove">&times;</button>
                            </div>
                            <details class="docling-options" id="docling-pdf-options" style="display:none">
                                <summary>Advanced Options</summary>
                                <div class="docling-options-grid">
                                    <label class="setting-toggle compact">
                                        <input type="checkbox" id="docling-fast-mode" checked>
                                        <span class="toggle-slider"></span>
                                        <span class="toggle-label">Fast table detection</span>
                                    </label>
                                    <label class="setting-toggle compact">
                                        <input type="checkbox" id="docling-gpu-mode">
                                        <span class="toggle-slider"></span>
                                        <span class="toggle-label">Use GPU (unloads Ollama models)</span>
                                    </label>
                                </div>
                            </details>
                            <div id="docling-resume-notice" class="docling-resume-notice hidden">
                                <span id="docling-resume-text"></span>
                                <label class="setting-toggle compact">
                                    <input type="checkbox" id="docling-resume-toggle" checked>
                                    <span class="toggle-slider"></span>
                                    <span class="toggle-label">Resume from last position</span>
                                </label>
                            </div>
                        </div>
                        <!-- Step 2: Progress -->
                        <div id="docling-step-progress" class="docling-step hidden">
                            <div class="docling-progress-info">
                                <div class="docling-progress-title" id="docling-progress-title">Preparing...</div>
                                <div class="docling-progress-detail" id="docling-progress-detail"></div>
                            </div>
                            <div class="docling-phase">
                                <div class="docling-phase-label" id="docling-phase-label">Converting</div>
                                <div class="docling-progress-bar-container">
                                    <div class="docling-progress-bar" id="docling-progress-bar"></div>
                                </div>
                            </div>
                            <div class="docling-progress-footer">
                                <div class="docling-progress-stats" id="docling-progress-stats"></div>
                                <div class="docling-timer" id="docling-timer">0:00</div>
                            </div>
                        </div>
                        <!-- Step 3: Complete -->
                        <div id="docling-step-complete" class="docling-step hidden">
                            <div class="docling-complete-icon">&#10003;</div>
                            <div class="docling-complete-text" id="docling-complete-text"></div>
                        </div>
                    </div>
                    <div class="field-editor-footer">
                        <span class="field-editor-error" id="docling-error"></span>
                        <button id="docling-import-cancel" class="btn-secondary">Cancel</button>
                        <button id="docling-import-submit" class="kb-create-btn" disabled>Import</button>
                    </div>
                </div>
            </div>
        </div>
    `;
    document.body.insertAdjacentHTML('beforeend', modalHTML);
}

/**
 * Cache DOM elements
 */
function cacheElements() {
    elements.overlay = document.getElementById('kb-manager-overlay');
    elements.panel = document.getElementById('kb-manager-panel');
    elements.closeBtn = document.getElementById('kb-manager-close');
    elements.backBtn = document.getElementById('kb-back-btn');
    elements.title = document.getElementById('kb-manager-title');

    // List view
    elements.listView = document.getElementById('kb-list-view');
    elements.searchInput = document.getElementById('kb-search');
    elements.createBtn = document.getElementById('kb-create-btn');
    elements.kbList = document.getElementById('kb-list');

    // Chunks view
    elements.chunksView = document.getElementById('kb-chunks-view');
    elements.chunkSearch = document.getElementById('chunk-search');
    elements.chunkCount = document.getElementById('chunk-count');
    elements.chunkList = document.getElementById('chunk-list');
    elements.firstBtn = document.getElementById('chunks-first');
    elements.prevBtn = document.getElementById('chunks-prev');
    elements.nextBtn = document.getElementById('chunks-next');
    elements.lastBtn = document.getElementById('chunks-last');
    elements.pageInfo = document.getElementById('chunks-page-info');
    elements.addChunkBtn = document.getElementById('add-chunk-btn');

    // Editor view
    elements.editorView = document.getElementById('kb-editor-view');
    elements.chunkContent = document.getElementById('chunk-content');
    elements.chunkSummary = document.getElementById('chunk-summary');
    elements.chunkQuestions = document.getElementById('chunk-questions');
    elements.cancelBtn = document.getElementById('chunk-cancel');
    elements.deleteChunkBtn = document.getElementById('chunk-delete');
    elements.saveBtn = document.getElementById('chunk-save');

    // Dialogs
    elements.createDialog = document.getElementById('kb-create-dialog');
    elements.deleteDialog = document.getElementById('kb-delete-dialog');
    elements.descDialog = document.getElementById('kb-desc-dialog');
}

/**
 * Setup event listeners
 */
function setupEventListeners() {
    // Close handlers
    elements.overlay?.addEventListener('click', closeKBManager);
    elements.closeBtn?.addEventListener('click', closeKBManager);
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modalOpen) {
            // Check if a dialog is open first
            const batchConfirm = document.getElementById('batch-delete-confirm');
            if (batchConfirm && !batchConfirm.classList.contains('hidden')) {
                batchConfirm.classList.add('hidden');
                return;
            }
            const doclingDialog = document.getElementById('docling-import-dialog');
            if (doclingDialog && !doclingDialog.classList.contains('hidden')) {
                cancelDoclingImport();
            } else if (!elements.createDialog.classList.contains('hidden')) {
                elements.createDialog.classList.add('hidden');
            } else if (!elements.deleteDialog.classList.contains('hidden')) {
                elements.deleteDialog.classList.add('hidden');
            } else if (!elements.descDialog.classList.contains('hidden')) {
                elements.descDialog.classList.add('hidden');
            } else if (currentView === 'editor') {
                showChunksView(true);
            } else if (currentView === 'chunks') {
                showListView();
            } else {
                closeKBManager();
            }
        }
    });

    // Navigation
    elements.backBtn?.addEventListener('click', handleBack);

    // Search
    elements.searchInput?.addEventListener('input', debounce(handleKBSearch, 300));
    elements.chunkSearch?.addEventListener('input', debounce(handleChunkSearch, 300));

    // Create KB
    elements.createBtn?.addEventListener('click', () => elements.createDialog.classList.remove('hidden'));
    document.getElementById('create-cancel')?.addEventListener('click', () => {
        elements.createDialog.classList.add('hidden');
        document.getElementById('new-kb-name').value = '';
        document.getElementById('new-kb-description').value = '';
    });
    document.getElementById('create-confirm')?.addEventListener('click', handleCreateKB);

    // Delete KB
    document.getElementById('delete-cancel')?.addEventListener('click', () => elements.deleteDialog.classList.add('hidden'));
    document.getElementById('delete-confirm')?.addEventListener('click', handleDeleteKB);

    // Edit description
    document.getElementById('desc-cancel')?.addEventListener('click', () => elements.descDialog.classList.add('hidden'));
    document.getElementById('desc-confirm')?.addEventListener('click', handleUpdateDescription);

    // Chunk pagination
    elements.firstBtn?.addEventListener('click', () => loadChunks(1));
    elements.prevBtn?.addEventListener('click', () => loadChunks(chunksData.page - 1));
    elements.nextBtn?.addEventListener('click', () => loadChunks(chunksData.page + 1));
    elements.lastBtn?.addEventListener('click', () => loadChunks(chunksData.pages));

    // Add chunk button
    elements.addChunkBtn?.addEventListener('click', showCreateChunkView);

    // Tab switching
    document.querySelectorAll('.kb-tab').forEach(tab => {
        tab.addEventListener('click', () => switchTab(tab.dataset.tab));
    });

    // Swipe left/right on chunk list to change pages
    let listSwipeStartX = 0;
    let listSwipeStartY = 0;
    elements.chunkList?.addEventListener('touchstart', (e) => {
        listSwipeStartX = e.touches[0].clientX;
        listSwipeStartY = e.touches[0].clientY;
    }, { passive: true });
    elements.chunkList?.addEventListener('touchend', (e) => {
        const deltaX = e.changedTouches[0].clientX - listSwipeStartX;
        const deltaY = e.changedTouches[0].clientY - listSwipeStartY;
        if (Math.abs(deltaX) < 50 || Math.abs(deltaY) > Math.abs(deltaX)) return;
        if (deltaX < 0 && chunksData.page < chunksData.pages) {
            loadChunks(chunksData.page + 1);
        } else if (deltaX > 0 && chunksData.page > 1) {
            loadChunks(chunksData.page - 1);
        }
    }, { passive: true });

    // Swipe left/right in editor view to cycle chunks
    let editorSwipeStartX = 0;
    let editorSwipeStartY = 0;
    elements.editorView?.addEventListener('touchstart', (e) => {
        editorSwipeStartX = e.touches[0].clientX;
        editorSwipeStartY = e.touches[0].clientY;
    }, { passive: true });
    elements.editorView?.addEventListener('touchend', (e) => {
        const deltaX = e.changedTouches[0].clientX - editorSwipeStartX;
        const deltaY = e.changedTouches[0].clientY - editorSwipeStartY;
        if (Math.abs(deltaX) < 50 || Math.abs(deltaY) > Math.abs(deltaX)) return;
        if (deltaX < 0) navigateNextChunk();
        else navigatePrevChunk();
    }, { passive: true });

    // Summary/Questions toggle checkboxes — enable/disable form fields
    document.getElementById('chunk-summary-toggle')?.addEventListener('change', (e) => {
        elements.chunkSummary.disabled = !e.target.checked;
        if (!e.target.checked) elements.chunkSummary.value = '';
    });
    document.getElementById('chunk-questions-toggle')?.addEventListener('change', (e) => {
        elements.chunkQuestions.disabled = !e.target.checked;
        if (!e.target.checked) elements.chunkQuestions.value = '';
    });

    // Batch delete
    document.getElementById('batch-delete-btn')?.addEventListener('click', showBatchDeleteConfirm);
    document.getElementById('batch-delete-cancel')?.addEventListener('click', () => {
        document.getElementById('batch-delete-confirm')?.classList.add('hidden');
    });
    document.getElementById('batch-delete-go')?.addEventListener('click', handleBatchDelete);

    // Docling import (from imports tab)
    document.getElementById('imports-import-btn')?.addEventListener('click', showDoclingImportDialog);
    document.getElementById('docling-import-close')?.addEventListener('click', hideDoclingImportDialog);
    document.getElementById('docling-import-cancel')?.addEventListener('click', cancelDoclingImport);
    document.getElementById('docling-import-submit')?.addEventListener('click', handleDoclingImport);
    document.getElementById('docling-file-clear')?.addEventListener('click', clearDoclingFile);

    const doclingFileInput = document.getElementById('docling-file-input');
    const doclingDropzone = document.getElementById('docling-dropzone');
    doclingDropzone?.addEventListener('click', () => doclingFileInput?.click());
    doclingDropzone?.addEventListener('dragover', (e) => {
        e.preventDefault();
        doclingDropzone.classList.add('dragover');
    });
    doclingDropzone?.addEventListener('dragleave', () => {
        doclingDropzone.classList.remove('dragover');
    });
    doclingDropzone?.addEventListener('drop', (e) => {
        e.preventDefault();
        doclingDropzone.classList.remove('dragover');
        const file = e.dataTransfer.files[0];
        if (file) setDoclingFile(file);
    });
    doclingFileInput?.addEventListener('change', () => {
        if (doclingFileInput.files[0]) setDoclingFile(doclingFileInput.files[0]);
    });

    // Chunk editor
    elements.cancelBtn?.addEventListener('click', () => showChunksView());
    elements.deleteChunkBtn?.addEventListener('click', handleDeleteChunk);
    elements.saveBtn?.addEventListener('click', handleSaveChunk);
}

/**
 * Open the KB Manager modal
 */
export async function openKBManager() {
    modalOpen = true;
    currentView = 'list';
    selectedKB = null;

    // Reset search
    if (elements.searchInput) elements.searchInput.value = '';

    // Show modal
    elements.overlay?.classList.add('active');
    elements.panel?.classList.add('open');
    document.body.style.overflow = 'hidden';

    // Load and render KBs
    await fetchKBs();
    renderKBList();
    showListView();
}

/**
 * Close the KB Manager modal
 */
export function closeKBManager() {
    modalOpen = false;
    elements.overlay?.classList.remove('active');
    elements.panel?.classList.remove('open');
    document.body.style.overflow = '';
}

/**
 * Fetch KBs from API
 */
async function fetchKBs() {
    try {
        const headers = getHeaders(false);
        const response = await fetch('/kb', { headers });
        if (!response.ok) throw new Error('Failed to fetch KBs');
        const data = await response.json();
        kbs = data.kbs || [];
    } catch (error) {
        console.error('Error fetching KBs:', error);
        kbs = [];
    }
}

/**
 * Render the KB list
 */
function renderKBList() {
    if (!elements.kbList) return;

    const searchTerm = elements.searchInput?.value.toLowerCase() || '';
    const filtered = kbs.filter(kb => kb.name.toLowerCase().includes(searchTerm));

    if (filtered.length === 0) {
        elements.kbList.innerHTML = `
            <div class="kb-empty">
                ${kbs.length === 0 ? 'No knowledge bases found' : 'No KBs match your search'}
            </div>
        `;
        return;
    }

    let html = '';
    for (const kb of filtered) {
        const displayName = formatDisplayName(kb.name);
        const descPreview = kb.description ? kb.description.substring(0, 80) + (kb.description.length > 80 ? '...' : '') : '';

        html += `
            <div class="kb-card" data-kb-name="${escapeHtml(kb.name)}">
                <div class="kb-card-info" data-action="browse">
                    <div class="kb-card-name">${escapeHtml(displayName)}</div>
                    <div class="kb-card-meta">
                        <span class="kb-chunk-count">${kb.chunks} chunks</span>
                        ${kb.embedding_model ? `<span class="kb-embed-model">${escapeHtml(kb.embedding_model)}</span>` : ''}
                        ${descPreview ? `<span class="kb-desc-preview">${escapeHtml(descPreview)}</span>` : ''}
                    </div>
                </div>
                <div class="kb-card-actions">
                    <button class="kb-action-btn" data-action="edit-desc" title="Edit description">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                    </button>
                    <button class="kb-action-btn kb-delete-btn" data-action="delete" title="Delete KB">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                    </button>
                </div>
            </div>
        `;
    }

    elements.kbList.innerHTML = html;

    // Add click handlers
    elements.kbList.querySelectorAll('.kb-card').forEach(card => {
        const kbName = card.dataset.kbName;

        // Click on info area browses chunks
        card.querySelector('[data-action="browse"]')?.addEventListener('click', () => {
            selectedKB = kbName;
            showChunksView();
        });

        // Edit description button
        card.querySelector('[data-action="edit-desc"]')?.addEventListener('click', (e) => {
            e.stopPropagation();
            const kb = kbs.find(k => k.name === kbName);
            document.getElementById('edit-kb-description').value = kb?.description || '';
            selectedKB = kbName;
            elements.descDialog.classList.remove('hidden');
        });

        // Delete button
        card.querySelector('[data-action="delete"]')?.addEventListener('click', (e) => {
            e.stopPropagation();
            selectedKB = kbName;
            document.getElementById('delete-kb-message').textContent =
                `Are you sure you want to delete "${formatDisplayName(kbName)}"?`;
            elements.deleteDialog.classList.remove('hidden');
        });
    });
}

/**
 * Show the list view
 */
function showListView() {
    currentView = 'list';
    elements.listView?.classList.remove('hidden');
    elements.chunksView?.classList.add('hidden');
    elements.editorView?.classList.add('hidden');
    elements.backBtn?.classList.add('hidden');
    elements.title.textContent = 'Knowledge Bases';
}

/**
 * Show the chunks view for selected KB
 */
async function showChunksView(restorePage) {
    currentView = 'chunks';
    elements.listView?.classList.add('hidden');
    elements.chunksView?.classList.remove('hidden');
    elements.editorView?.classList.add('hidden');
    elements.backBtn?.classList.remove('hidden');
    elements.title.textContent = formatDisplayName(selectedKB);

    // Reset to chunks tab
    switchTab('chunks');

    if (restorePage) {
        // Returning from editor — reload same page and restore scroll
        await loadChunks(chunksData.page || 1);
        requestAnimationFrame(() => {
            if (elements.chunkList) elements.chunkList.scrollTop = savedScrollTop;
        });
    } else {
        // Fresh entry — reset search and load from page 1
        if (elements.chunkSearch) elements.chunkSearch.value = '';
        await Promise.all([loadChunks(1), loadSourceFiles()]);
    }
}

/**
 * Switch between Chunks and Imports tabs
 */
function switchTab(tab) {
    currentTab = tab;
    document.querySelectorAll('.kb-tab').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.tab === tab);
    });
    const chunksTab = document.getElementById('kb-tab-chunks');
    const importsTab = document.getElementById('kb-tab-imports');
    if (chunksTab) chunksTab.classList.toggle('hidden', tab !== 'chunks');
    if (importsTab) importsTab.classList.toggle('hidden', tab !== 'imports');
}

/**
 * Show the chunk editor for editing an existing chunk
 */
function showEditorView(chunk) {
    // Save scroll position before switching views
    savedScrollTop = elements.chunkList?.scrollTop || 0;

    currentView = 'editor';
    editingChunk = chunk;
    isCreatingChunk = false;

    elements.listView?.classList.add('hidden');
    elements.chunksView?.classList.add('hidden');
    elements.editorView?.classList.remove('hidden');
    elements.backBtn?.classList.remove('hidden');

    // Build title with prev/next nav — use display number (1-based sequential)
    const chunkIds = chunksData.chunks.map(c => c.id);
    const idx = chunkIds.indexOf(chunk.id);
    const limit = 20;
    const displayNum = idx >= 0 ? (chunksData.page - 1) * limit + idx + 1 : chunk.id;
    const hasPrev = idx > 0 || chunksData.page > 1;
    const hasNext = idx < chunkIds.length - 1 || chunksData.page < chunksData.pages;
    elements.title.innerHTML = `
        <button id="editor-prev-chunk" class="editor-nav-btn" ${hasPrev ? '' : 'disabled'} title="Previous chunk">&lsaquo;</button>
        <span>Chunk #${displayNum}</span>
        <button id="editor-next-chunk" class="editor-nav-btn" ${hasNext ? '' : 'disabled'} title="Next chunk">&rsaquo;</button>
    `;
    document.getElementById('editor-prev-chunk')?.addEventListener('click', navigatePrevChunk);
    document.getElementById('editor-next-chunk')?.addEventListener('click', navigateNextChunk);

    // Populate fields
    elements.chunkContent.value = chunk.content || '';
    elements.chunkSummary.value = chunk.summary || '';
    elements.chunkQuestions.value = (chunk.questions || []).join('\n');

    // Toggle summary/questions enabled state based on content
    const summaryToggle = document.getElementById('chunk-summary-toggle');
    const questionsToggle = document.getElementById('chunk-questions-toggle');
    const hasSummary = !!(chunk.summary);
    const hasQuestions = !!(chunk.questions?.length);
    if (summaryToggle) {
        summaryToggle.checked = hasSummary;
        elements.chunkSummary.disabled = !hasSummary;
    }
    if (questionsToggle) {
        questionsToggle.checked = hasQuestions;
        elements.chunkQuestions.disabled = !hasQuestions;
    }

    // Show delete button and change save button text for edit mode
    elements.deleteChunkBtn.classList.remove('hidden');
    elements.saveBtn.textContent = 'Save Changes';
}

/**
 * Show the chunk editor for creating a new chunk
 */
function showCreateChunkView() {
    currentView = 'editor';
    editingChunk = null;
    isCreatingChunk = true;

    elements.listView?.classList.add('hidden');
    elements.chunksView?.classList.add('hidden');
    elements.editorView?.classList.remove('hidden');
    elements.backBtn?.classList.remove('hidden');
    elements.title.textContent = 'New Chunk';

    // Clear fields and disable optional sections
    elements.chunkContent.value = '';
    elements.chunkSummary.value = '';
    elements.chunkQuestions.value = '';
    const summaryToggle = document.getElementById('chunk-summary-toggle');
    const questionsToggle = document.getElementById('chunk-questions-toggle');
    if (summaryToggle) { summaryToggle.checked = false; elements.chunkSummary.disabled = true; }
    if (questionsToggle) { questionsToggle.checked = false; elements.chunkQuestions.disabled = true; }

    // Hide delete button and change save button text for create mode
    elements.deleteChunkBtn.classList.add('hidden');
    elements.saveBtn.textContent = 'Create Chunk';
}

/**
 * Load chunks for current KB
 */
async function loadChunks(page = 1) {
    if (!selectedKB) return;

    const search = elements.chunkSearch?.value || '';
    const limit = 20;

    try {
        const headers = getHeaders(false);

        const params = new URLSearchParams({ page, limit });
        if (search) params.append('search', search);

        const response = await fetch(`/kb/${encodeURIComponent(selectedKB)}/chunks?${params}`, { headers });
        if (!response.ok) throw new Error('Failed to fetch chunks');

        chunksData = await response.json();
        renderChunkList();
    } catch (error) {
        console.error('Error fetching chunks:', error);
        elements.chunkList.innerHTML = '<div class="kb-empty">Error loading chunks</div>';
    }
}

/**
 * Render the chunk list
 */
function renderChunkList() {
    if (!elements.chunkList) return;

    const { chunks, total, page, pages } = chunksData;

    // Update count and pagination
    elements.chunkCount.textContent = `${total} chunks`;
    elements.pageInfo.innerHTML = `<input type="number" id="page-jump-input" class="page-jump-input" value="${page}" min="1" max="${pages}"> of ${pages}`;
    elements.firstBtn.disabled = page <= 1;
    elements.prevBtn.disabled = page <= 1;
    elements.nextBtn.disabled = page >= pages;
    elements.lastBtn.disabled = page >= pages;

    // Page jump on Enter or blur
    const pageInput = document.getElementById('page-jump-input');
    if (pageInput) {
        pageInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                const target = Math.max(1, Math.min(pages, parseInt(pageInput.value) || 1));
                loadChunks(target);
            }
        });
        pageInput.addEventListener('blur', () => {
            const target = Math.max(1, Math.min(pages, parseInt(pageInput.value) || 1));
            if (target !== page) loadChunks(target);
        });
    }

    if (chunks.length === 0) {
        elements.chunkList.innerHTML = '<div class="kb-empty">No chunks found</div>';
        return;
    }

    let html = '';
    const limit = 20;
    for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        const displayNum = (page - 1) * limit + i + 1;  // 1-based sequential
        const preview = (chunk.content_preview || '').replace(/\n/g, ' ').substring(0, 80);
        const pagesStr = chunk.pages?.length ? `p.${chunk.pages.join(',')}` : '';

        html += `
            <div class="chunk-item" data-chunk-id="${chunk.id}" data-display-num="${displayNum}">
                <div class="chunk-item-header">
                    <label class="chunk-select-label" onclick="event.stopPropagation()">
                        <input type="checkbox" class="chunk-select-cb" data-chunk-id="${chunk.id}">
                    </label>
                    <span class="chunk-id">#${displayNum}</span>
                    ${pagesStr ? `<span class="chunk-pages">${pagesStr}</span>` : ''}
                </div>
                <div class="chunk-preview">${escapeHtml(preview)}...</div>
                ${chunk.summary ? `<div class="chunk-summary">${escapeHtml(chunk.summary)}</div>` : ''}
            </div>
        `;
    }

    elements.chunkList.innerHTML = html;

    // Update batch delete button visibility
    updateBatchDeleteBtn();

    // Checkbox change handler for batch selection
    elements.chunkList.querySelectorAll('.chunk-select-cb').forEach(cb => {
        cb.addEventListener('change', updateBatchDeleteBtn);
    });

    // Add click handlers (on the item, not the checkbox)
    elements.chunkList.querySelectorAll('.chunk-item').forEach(item => {
        item.addEventListener('click', async (e) => {
            if (e.target.closest('.chunk-select-label')) return;  // Skip checkbox clicks
            const chunkId = parseInt(item.dataset.chunkId);
            await loadAndEditChunk(chunkId);
        });
    });
}

/**
 * Load full chunk data and show editor
 */
async function loadAndEditChunk(chunkId) {
    try {
        const headers = getHeaders(false);

        const response = await fetch(`/kb/${encodeURIComponent(selectedKB)}/chunks/${chunkId}`, { headers });
        if (!response.ok) throw new Error('Failed to fetch chunk');

        const chunk = await response.json();
        showEditorView(chunk);
    } catch (error) {
        console.error('Error fetching chunk:', error);
        alert('Failed to load chunk');
    }
}

/**
 * Update batch delete button visibility based on checkbox selection
 */
function updateBatchDeleteBtn() {
    const btn = document.getElementById('batch-delete-btn');
    if (!btn) return;
    const checked = document.querySelectorAll('.chunk-select-cb:checked');
    btn.classList.toggle('hidden', checked.length === 0);
    btn.textContent = checked.length > 0 ? `Delete ${checked.length} Selected` : 'Delete Selected';
}

/**
 * Show batch delete confirmation
 */
function showBatchDeleteConfirm() {
    const checked = document.querySelectorAll('.chunk-select-cb:checked');
    if (checked.length === 0) return;
    const msg = document.getElementById('batch-delete-msg');
    if (msg) msg.textContent = `Delete ${checked.length} chunk${checked.length > 1 ? 's' : ''}? This cannot be undone.`;
    document.getElementById('batch-delete-confirm')?.classList.remove('hidden');
}

/**
 * Execute batch delete
 */
async function handleBatchDelete() {
    const checked = document.querySelectorAll('.chunk-select-cb:checked');
    const ids = [...checked].map(cb => parseInt(cb.dataset.chunkId));
    document.getElementById('batch-delete-confirm')?.classList.add('hidden');

    const headers = getHeaders(true);
    let deleted = 0;
    for (const id of ids) {
        try {
            const res = await fetch(`/kb/${encodeURIComponent(selectedKB)}/chunks/${id}`, {
                method: 'DELETE', headers
            });
            if (res.ok) deleted++;
        } catch (e) { console.error(`Failed to delete chunk ${id}:`, e); }
    }

    if (deleted > 0) {
        await loadChunks(chunksData.page);
    }
}

/**
 * Navigate to the previous chunk in the list (or last chunk of previous page)
 */
async function navigatePrevChunk() {
    if (!editingChunk) return;
    const chunkIds = chunksData.chunks.map(c => c.id);
    const idx = chunkIds.indexOf(editingChunk.id);
    if (idx > 0) {
        await loadAndEditChunk(chunkIds[idx - 1]);
    } else if (chunksData.page > 1) {
        await loadChunks(chunksData.page - 1);
        const lastId = chunksData.chunks[chunksData.chunks.length - 1]?.id;
        if (lastId != null) await loadAndEditChunk(lastId);
    }
}

/**
 * Navigate to the next chunk in the list (or first chunk of next page)
 */
async function navigateNextChunk() {
    if (!editingChunk) return;
    const chunkIds = chunksData.chunks.map(c => c.id);
    const idx = chunkIds.indexOf(editingChunk.id);
    if (idx < chunkIds.length - 1) {
        await loadAndEditChunk(chunkIds[idx + 1]);
    } else if (chunksData.page < chunksData.pages) {
        await loadChunks(chunksData.page + 1);
        const firstId = chunksData.chunks[0]?.id;
        if (firstId != null) await loadAndEditChunk(firstId);
    }
}

/**
 * Handle back button
 */
function handleBack() {
    if (currentView === 'editor') {
        showChunksView(true);  // Restore page + scroll position
    } else if (currentView === 'chunks') {
        showListView();
    }
}

/**
 * Handle KB search
 */
function handleKBSearch() {
    renderKBList();
}

/**
 * Handle chunk search
 */
function handleChunkSearch() {
    loadChunks(1);
}

/**
 * Handle create KB
 */
async function handleCreateKB() {
    const name = document.getElementById('new-kb-name').value.trim();
    const description = document.getElementById('new-kb-description').value.trim();

    if (!name) {
        alert('Please enter a KB name');
        return;
    }

    try {
        const headers = getHeaders();

        const params = new URLSearchParams({ name });
        if (description) params.append('description', description);

        const response = await fetch(`/kb/create?${params}`, {
            method: 'POST',
            headers
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to create KB');
        }

        // Close dialog and refresh
        elements.createDialog.classList.add('hidden');
        document.getElementById('new-kb-name').value = '';
        document.getElementById('new-kb-description').value = '';

        await fetchKBs();
        renderKBList();
        updateKBCountBadge();
    } catch (error) {
        alert(error.message);
    }
}

/**
 * Handle delete KB
 */
async function handleDeleteKB() {
    if (!selectedKB) return;

    try {
        const headers = getHeaders(false);

        const response = await fetch(`/kb/${encodeURIComponent(selectedKB)}`, {
            method: 'DELETE',
            headers
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to delete KB');
        }

        elements.deleteDialog.classList.add('hidden');
        selectedKB = null;

        await fetchKBs();
        renderKBList();
        updateKBCountBadge();
    } catch (error) {
        alert(error.message);
    }
}

/**
 * Handle update description
 */
async function handleUpdateDescription() {
    if (!selectedKB) return;

    const description = document.getElementById('edit-kb-description').value.trim();

    try {
        const headers = getHeaders();

        const response = await fetch(`/kb/${encodeURIComponent(selectedKB)}/description?description=${encodeURIComponent(description)}`, {
            method: 'PUT',
            headers
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to update description');
        }

        elements.descDialog.classList.add('hidden');

        await fetchKBs();
        renderKBList();
    } catch (error) {
        alert(error.message);
    }
}

/**
 * Handle save chunk (create or update)
 */
async function handleSaveChunk() {
    if (!selectedKB) return;

    const content = elements.chunkContent.value.trim();
    const summary = elements.chunkSummary.value.trim();
    const questionsText = elements.chunkQuestions.value;
    const questions = questionsText.split('\n').map(q => q.trim()).filter(q => q);

    if (!content) {
        alert('Content is required');
        return;
    }

    try {
        const headers = getHeaders();

        let response;

        if (isCreatingChunk) {
            // Create new chunk
            elements.saveBtn.textContent = 'Creating...';
            elements.saveBtn.disabled = true;

            response = await fetch(`/kb/${encodeURIComponent(selectedKB)}/chunks`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    content,
                    summary: summary || null,
                    questions: questions.length ? questions : null
                })
            });
        } else {
            // Update existing chunk
            if (!editingChunk) return;

            elements.saveBtn.textContent = 'Saving...';
            elements.saveBtn.disabled = true;

            response = await fetch(`/kb/${encodeURIComponent(selectedKB)}/chunks/${editingChunk.id}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify({ content, summary, questions })
            });
        }

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to save chunk');
        }

        showChunksView();
    } catch (error) {
        alert(error.message);
    } finally {
        // Reset button state
        elements.saveBtn.disabled = false;
        elements.saveBtn.textContent = isCreatingChunk ? 'Create Chunk' : 'Save Changes';
    }
}

/**
 * Handle delete chunk
 */
async function handleDeleteChunk() {
    if (!selectedKB || !editingChunk) return;

    const confirmed = await confirm({
        title: 'Delete Chunk',
        message: `Delete chunk #${editingChunk.id}? This cannot be undone.`,
        confirmText: 'Delete',
        danger: true
    });
    if (!confirmed) return;

    try {
        const headers = getHeaders(false);

        const response = await fetch(`/kb/${encodeURIComponent(selectedKB)}/chunks/${editingChunk.id}`, {
            method: 'DELETE',
            headers
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to delete chunk');
        }

        editingChunk = null;
        showChunksView();
    } catch (error) {
        alert(error.message);
    }
}

/**
 * Update KB count badge in menu
 */
async function updateKBCountBadge() {
    const count = await getKBCount();
    const badge = document.getElementById('kb-count-badge');
    if (badge) badge.textContent = count;
}

// ============================================================================
// Source File Management
// ============================================================================

let sourceFiles = [];

/**
 * Load source files for the current KB
 */
async function loadSourceFiles() {
    if (!selectedKB) return;

    try {
        const headers = getHeaders(false);

        const response = await fetch(`/kb/${encodeURIComponent(selectedKB)}/sources`, { headers });
        if (!response.ok) {
            sourceFiles = [];
            renderSourceFiles();
            return;
        }

        const data = await response.json();
        sourceFiles = data.files || [];
        renderSourceFiles();
    } catch (error) {
        console.error('Error loading source files:', error);
        sourceFiles = [];
        renderSourceFiles();
    }
}

/**
 * Render the source files list in the Imports tab
 */
function renderSourceFiles() {
    const list = document.getElementById('imports-list');
    const count = document.getElementById('imports-count');
    if (!list) return;

    if (count) count.textContent = `${sourceFiles.length} file${sourceFiles.length !== 1 ? 's' : ''}`;

    if (sourceFiles.length === 0) {
        list.innerHTML = '<div class="kb-empty">No imported files. Click Import to add documents.</div>';
        return;
    }

    let html = '';
    for (const file of sourceFiles) {
        const size = formatFileSize(file.file_size);
        const date = file.imported_at ? new Date(file.imported_at).toLocaleDateString() : '';
        const chunkRange = file.chunk_start && file.chunk_end
            ? `Chunks #${file.chunk_start}\u2013#${file.chunk_end}`
            : `${file.chunk_count || '?'} chunks`;

        html += `
            <div class="source-file-item" data-filename="${escapeHtml(file.filename)}">
                <div class="source-file-info">
                    <span class="source-file-name">${escapeHtml(file.filename)}</span>
                    <span class="source-file-meta">${size} \u00b7 ${date} \u00b7 ${chunkRange}</span>
                </div>
                <div class="source-file-actions">
                    <button class="source-file-download" title="Download" data-filename="${escapeHtml(file.filename)}">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    </button>
                    <button class="source-file-delete" title="Delete" data-filename="${escapeHtml(file.filename)}" data-chunk-start="${file.chunk_start || ''}" data-chunk-end="${file.chunk_end || ''}">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                    </button>
                </div>
            </div>
        `;
    }

    list.innerHTML = html;

    // Add event listeners
    list.querySelectorAll('.source-file-download').forEach(btn => {
        btn.addEventListener('click', () => downloadSourceFile(btn.dataset.filename));
    });
    list.querySelectorAll('.source-file-delete').forEach(btn => {
        btn.addEventListener('click', () => deleteSourceFile(
            btn.dataset.filename,
            btn.dataset.chunkStart ? parseInt(btn.dataset.chunkStart) : null,
            btn.dataset.chunkEnd ? parseInt(btn.dataset.chunkEnd) : null
        ));
    });
}

/**
 * Download a source file via blob
 */
async function downloadSourceFile(filename) {
    try {
        const headers = getHeaders(false);

        const response = await fetch(
            `/kb/${encodeURIComponent(selectedKB)}/sources/${encodeURIComponent(filename)}/download`,
            { headers }
        );

        if (!response.ok) throw new Error('Download failed');

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Download error:', error);
        alert('Failed to download file');
    }
}

/**
 * Delete a source file with optional chunk deletion
 */
async function deleteSourceFile(filename, chunkStart, chunkEnd) {
    const hasChunks = chunkStart && chunkEnd;
    const chunkCount = hasChunks ? chunkEnd - chunkStart + 1 : 0;

    const result = await confirm({
        title: 'Delete Source File',
        message: `Delete "${filename}"? This cannot be undone.`,
        confirmText: 'Delete',
        danger: true,
        ...(hasChunks ? {
            checkbox: `Also delete ${chunkCount} associated chunks (#${chunkStart}\u2013#${chunkEnd})`
        } : {})
    });

    // With checkbox: result is {confirmed, checked}. Without: result is boolean.
    const confirmed = hasChunks ? result.confirmed : result;
    const deleteChunks = hasChunks ? result.checked : false;
    if (!confirmed) return;

    try {
        const headers = getHeaders(false);

        const params = deleteChunks ? '?delete_chunks=true' : '';
        const url = `/kb/${encodeURIComponent(selectedKB)}/sources/${encodeURIComponent(filename)}${params}`;
        const response = await fetch(url, { method: 'DELETE', headers });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to delete file');
        }

        const result2 = await response.json();
        const chunksDeleted = result2.chunks_deleted || 0;

        await loadSourceFiles();
        if (chunksDeleted > 0) {
            await loadChunks(1);
            showToast(`Deleted "${filename}" and ${chunksDeleted} chunks`);
        } else {
            showToast(`Deleted "${filename}"`);
        }
    } catch (error) {
        console.error('Delete error:', error);
        showToast(error.message, 'error');
    }
}

/**
 * Format file size in human-readable form
 */
function formatFileSize(bytes) {
    if (!bytes || bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return (bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0) + ' ' + units[i];
}

// ============================================================================
// Docling Import Functions
// ============================================================================

function setDoclingFile(file) {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!DOCLING_ALLOWED_EXT.has(ext)) {
        const errorEl = document.getElementById('docling-error');
        if (errorEl) errorEl.textContent = `Unsupported file type: ${ext}`;
        return;
    }
    doclingFile = file;
    doclingUploadMeta = null;

    const dropzone = document.getElementById('docling-dropzone');
    const fileInfo = document.getElementById('docling-file-info');
    const fileName = document.getElementById('docling-file-name');
    if (dropzone) dropzone.classList.add('hidden');
    if (fileInfo) fileInfo.classList.remove('hidden');
    if (fileName) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        fileName.textContent = `${file.name} (${sizeMB} MB)`;
    }

    // Show/hide PDF-only options
    const pdfOptions = document.getElementById('docling-pdf-options');
    if (pdfOptions) pdfOptions.style.display = ext === '.pdf' ? '' : 'none';

    // Clear resume notice & errors
    document.getElementById('docling-resume-notice')?.classList.add('hidden');
    const errorEl = document.getElementById('docling-error');
    if (errorEl) errorEl.textContent = '';

    // Enable submit
    const submitBtn = document.getElementById('docling-import-submit');
    if (submitBtn) submitBtn.disabled = false;
}

function clearDoclingFile() {
    doclingFile = null;
    doclingUploadMeta = null;
    const dropzone = document.getElementById('docling-dropzone');
    const fileInfo = document.getElementById('docling-file-info');
    const submitBtn = document.getElementById('docling-import-submit');
    const fileInput = document.getElementById('docling-file-input');
    if (dropzone) dropzone.classList.remove('hidden');
    if (fileInfo) fileInfo.classList.add('hidden');
    if (submitBtn) submitBtn.disabled = true;
    document.getElementById('docling-pdf-options').style.display = 'none';
    document.getElementById('docling-resume-notice')?.classList.add('hidden');
    const errorEl = document.getElementById('docling-error');
    if (errorEl) errorEl.textContent = '';
    if (fileInput) fileInput.value = '';
}

function showDoclingImportDialog() {
    document.getElementById('docling-step-upload')?.classList.remove('hidden');
    document.getElementById('docling-step-progress')?.classList.add('hidden');
    document.getElementById('docling-step-complete')?.classList.add('hidden');
    clearDoclingFile();

    const cancelBtn = document.getElementById('docling-import-cancel');
    const submitBtn = document.getElementById('docling-import-submit');
    if (cancelBtn) { cancelBtn.textContent = 'Cancel'; cancelBtn.disabled = false; }
    if (submitBtn) { submitBtn.textContent = 'Import'; submitBtn.disabled = true; submitBtn.classList.remove('hidden'); }

    document.getElementById('docling-import-dialog')?.classList.remove('hidden');
}

function hideDoclingImportDialog() {
    if (doclingImportActive) return;
    document.getElementById('docling-import-dialog')?.classList.add('hidden');
    clearDoclingFile();
}

function cancelDoclingImport() {
    if (doclingImportActive && doclingAbortController) {
        doclingAbortController.abort();
        doclingImportActive = false;
    }
    document.getElementById('docling-import-dialog')?.classList.add('hidden');
    clearDoclingFile();
}

async function handleDoclingImport() {
    if (!doclingFile || !selectedKB) return;

    const errorEl = document.getElementById('docling-error');
    const submitBtn = document.getElementById('docling-import-submit');
    const cancelBtn = document.getElementById('docling-import-cancel');
    if (errorEl) errorEl.textContent = '';
    if (submitBtn) { submitBtn.disabled = true; submitBtn.innerHTML = '<span class="btn-spinner"></span> Importing...'; }

    // Step 1: Upload file
    try {
        const formData = new FormData();
        formData.append('file', doclingFile);

        const headers = getHeaders(false);

        const uploadRes = await fetch('/import/docling/upload', {
            method: 'POST', headers, body: formData
        });

        if (!uploadRes.ok) {
            const data = await uploadRes.json().catch(() => ({}));
            throw new Error(data.detail || 'Import failed');
        }

        doclingUploadMeta = await uploadRes.json();

        // Override kb_name with the currently selected KB
        doclingUploadMeta.kb_name = selectedKB;

        // Check for resume
        if (doclingUploadMeta.resume_from && doclingUploadMeta.resume_from > 1) {
            const resumeNotice = document.getElementById('docling-resume-notice');
            const resumeText = document.getElementById('docling-resume-text');
            if (resumeNotice && resumeText) {
                resumeText.textContent = `Previous import found: ${doclingUploadMeta.chunks_so_far} chunks from pages 1-${doclingUploadMeta.resume_from - 1}. `;
                resumeNotice.classList.remove('hidden');
            }
        }
    } catch (err) {
        if (errorEl) errorEl.textContent = err.message;
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Import'; }
        return;
    }

    // Step 2: Switch to progress view
    document.getElementById('docling-step-upload')?.classList.add('hidden');
    document.getElementById('docling-step-progress')?.classList.remove('hidden');
    if (submitBtn) submitBtn.classList.add('hidden');
    if (cancelBtn) cancelBtn.textContent = 'Cancel Import';

    const progressTitle = document.getElementById('docling-progress-title');
    const progressDetail = document.getElementById('docling-progress-detail');
    const progressBar = document.getElementById('docling-progress-bar');
    const phaseLabel = document.getElementById('docling-phase-label');
    const progressStats = document.getElementById('docling-progress-stats');
    if (progressTitle) progressTitle.textContent = 'Starting import...';
    if (progressDetail) { progressDetail.textContent = ''; progressDetail.style.color = ''; }
    if (progressBar) progressBar.style.width = '0%';
    if (phaseLabel) phaseLabel.textContent = 'Converting';
    if (progressStats) progressStats.textContent = '';

    // Start elapsed timer
    doclingTimerStart = Date.now();
    const timerEl = document.getElementById('docling-timer');
    if (timerEl) timerEl.textContent = '0:00';
    if (doclingTimerInterval) clearInterval(doclingTimerInterval);
    doclingTimerInterval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - doclingTimerStart) / 1000);
        const mins = Math.floor(elapsed / 60);
        const secs = String(elapsed % 60).padStart(2, '0');
        if (timerEl) timerEl.textContent = `${mins}:${secs}`;
    }, 1000);

    // Build import request — import into the current KB (no agent auto-assign)
    const isPdf = doclingUploadMeta.file_type === 'pdf';
    const resumeToggle = document.getElementById('docling-resume-toggle');
    const shouldResume = resumeToggle?.checked && doclingUploadMeta.resume_from > 1;

    const importReq = {
        file_path: doclingUploadMeta.file_path,
        kb_name: selectedKB,
    };

    if (isPdf) {
        importReq.fast_mode = document.getElementById('docling-fast-mode')?.checked ?? true;
        importReq.use_gpu = document.getElementById('docling-gpu-mode')?.checked ?? false;
        if (shouldResume) importReq.start_page = doclingUploadMeta.resume_from;
        if (doclingUploadMeta.total_pages) importReq.end_page = doclingUploadMeta.total_pages;
    }

    // Step 3: Start SSE stream
    doclingImportActive = true;
    doclingAbortController = new AbortController();

    try {
        const fetchHeaders = getHeaders();

        const response = await fetch('/import/docling', {
            method: 'POST',
            headers: fetchHeaders,
            body: JSON.stringify(importReq),
            signal: doclingAbortController.signal
        });

        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.detail || 'Import request failed');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let currentEvent = 'message';

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                if (line.startsWith('event: ')) {
                    currentEvent = line.slice(7).trim();
                } else if (line.startsWith('data: ')) {
                    try {
                        const data = JSON.parse(line.slice(6));
                        handleDoclingEvent(currentEvent, data);
                    } catch (e) { /* skip non-JSON */ }
                    currentEvent = 'message';
                }
            }
        }
    } catch (err) {
        if (err.name === 'AbortError') return;
        if (errorEl) errorEl.textContent = err.message;
        if (progressTitle) progressTitle.textContent = 'Import failed';
        if (progressDetail) { progressDetail.textContent = err.message; progressDetail.style.color = '#ef4444'; }
        if (cancelBtn) cancelBtn.textContent = 'Close';
    } finally {
        doclingImportActive = false;
        doclingAbortController = null;
        if (doclingTimerInterval) { clearInterval(doclingTimerInterval); doclingTimerInterval = null; }
    }
}

function handleDoclingEvent(event, data) {
    const progressTitle = document.getElementById('docling-progress-title');
    const progressDetail = document.getElementById('docling-progress-detail');
    const progressBar = document.getElementById('docling-progress-bar');
    const phaseLabel = document.getElementById('docling-phase-label');
    const progressStats = document.getElementById('docling-progress-stats');

    switch (event) {
        case 'info':
            if (data.file_type === 'pdf') {
                if (progressTitle) progressTitle.textContent = `Importing PDF (${data.total_pages} pages)`;
            } else {
                if (progressTitle) progressTitle.textContent = `Importing ${data.filename || 'document'}...`;
            }
            if (phaseLabel) phaseLabel.textContent = 'Converting';
            break;

        case 'batch_start':
            break;

        case 'batch_progress': {
            // Real progress from subprocess JSONL
            // percent 0-70 = conversion phase, 72-100 = importing phase
            const pct = data.percent || 0;
            if (pct <= 70) {
                // Converting phase: scale 0-70 → 0-100
                if (phaseLabel && phaseLabel.textContent !== 'Converting') phaseLabel.textContent = 'Converting';
                const barPct = Math.round((pct / 70) * 100);
                if (progressBar) progressBar.style.width = `${barPct}%`;
            } else {
                // Importing phase: reset bar on transition, scale 72-100 → 0-100
                if (phaseLabel && phaseLabel.textContent !== 'Importing') {
                    phaseLabel.textContent = 'Importing';
                    // Brief reset so user sees bar shrink then grow
                    if (progressBar) { progressBar.style.transition = 'none'; progressBar.style.width = '0%'; }
                    requestAnimationFrame(() => {
                        if (progressBar) progressBar.style.transition = 'width 0.4s ease';
                    });
                }
                const barPct = Math.round(((pct - 70) / 30) * 100);
                // Delay slightly so the reset to 0 renders first
                setTimeout(() => {
                    if (progressBar) progressBar.style.width = `${Math.min(barPct, 100)}%`;
                }, 50);
            }
            if (data.stage && progressDetail) progressDetail.textContent = data.stage;
            break;
        }

        case 'batch_complete': {
            if (progressStats) progressStats.textContent = `${data.total_chunks} chunks`;
            break;
        }

        case 'progress': {
            // Non-PDF formats (single-phase import)
            const pct = data.percent || 0;
            if (phaseLabel) phaseLabel.textContent = 'Importing';
            if (progressBar) progressBar.style.width = `${pct}%`;
            if (progressDetail) progressDetail.textContent = data.stage || '';
            break;
        }

        case 'batch_error':
            if (doclingTimerInterval) { clearInterval(doclingTimerInterval); doclingTimerInterval = null; }
            if (progressDetail) { progressDetail.textContent = `Error: ${data.error}`; progressDetail.style.color = '#ef4444'; }
            break;

        case 'batch_timeout':
            if (doclingTimerInterval) { clearInterval(doclingTimerInterval); doclingTimerInterval = null; }
            if (progressDetail) { progressDetail.textContent = `Timed out at page ${data.start_page}. Progress saved.`; progressDetail.style.color = '#fbbf24'; }
            break;

        case 'complete': {
            if (doclingTimerInterval) { clearInterval(doclingTimerInterval); doclingTimerInterval = null; }
            document.getElementById('docling-step-progress')?.classList.add('hidden');
            document.getElementById('docling-step-complete')?.classList.remove('hidden');

            const completeText = document.getElementById('docling-complete-text');
            const elapsed = data.elapsed_seconds ? `${data.elapsed_seconds.toFixed(1)}s` : '';
            let summary = `Successfully imported <strong>${data.total_chunks}</strong> chunks`;
            if (elapsed) summary += ` in ${elapsed}`;
            if (completeText) completeText.innerHTML = summary;

            // Hide submit, show Close
            const submitBtn = document.getElementById('docling-import-submit');
            if (submitBtn) submitBtn.classList.add('hidden');
            const cancelBtn = document.getElementById('docling-import-cancel');
            if (cancelBtn) { cancelBtn.textContent = 'Close'; cancelBtn.disabled = false; }
            doclingImportActive = false;

            // Refresh chunk list and source files, then auto-close after a brief pause
            loadChunks(1);
            loadSourceFiles();
            setTimeout(() => {
                document.getElementById('docling-import-dialog')?.classList.add('hidden');
            }, 2000);
            break;
        }

        case 'error': {
            if (progressTitle) progressTitle.textContent = 'Import failed';
            if (progressDetail) { progressDetail.textContent = data.message || 'Unknown error'; progressDetail.style.color = '#ef4444'; }
            const errorEl = document.getElementById('docling-error');
            if (errorEl) errorEl.textContent = data.message || 'Import failed';
            const cancelBtn = document.getElementById('docling-import-cancel');
            if (cancelBtn) { cancelBtn.textContent = 'Close'; cancelBtn.disabled = false; }
            doclingImportActive = false;
            break;
        }

        case 'done':
            doclingImportActive = false;
            break;
    }
}

// Utility functions

function formatDisplayName(name) {
    return name
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}


function debounce(fn, delay) {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn(...args), delay);
    };
}
